import { FunctionComponent, useCallback, useMemo } from 'react'
import useStyles from './styles'
import { RadioProps } from './types'
import { useStateStyles } from './utils'
import { ReactComponent as CheckedIcon } from '@/assets/radio-checked.svg'
import { useRadioGroup } from '../RadioGroup/RadioGroupContext'
import { createChainedFunction } from '@/utils/createChainedFunction'

export const Radio: FunctionComponent<RadioProps> = ({
  styles: overrides,
  onChange: onChangeProp,
  checked,
  disabled,
  name: propName,
  inputProps,
  inputRef,
  value,
}) => {
  const styles = useStyles({ disabled, checkedOverrides: overrides?.checked })
  const radioGroup = useRadioGroup()
  const { disabledStyles } = useStateStyles({
    styles,
    overrides,
    disabled,
  })

  const name = useMemo(() => radioGroup?.name || propName, [
    propName,
    radioGroup?.name,
  ])

  const onChange = useMemo(() => {
    return createChainedFunction(onChangeProp, radioGroup?.onChange)
  }, [onChangeProp, radioGroup])

  const forkRefs = useCallback(
    elRef => {
      if (inputRef) {
        if (typeof inputRef === 'function') {
          inputRef(elRef)
        } else {
          inputRef.current = elRef
        }
      }
      if (radioGroup?.ref) {
        if (typeof radioGroup.ref === 'function') {
          console.error('The RadioGroup ref must to be a MutableRefObject')
        } else {
          const ref = radioGroup.ref
          if (!ref.current?.inputs) {
            ref.current = {
              inputs: {},
            }
          }
          Object.assign(ref.current.inputs, {
            [value || elRef.id]: elRef,
          })
        }
      }
    },
    [inputRef, radioGroup?.ref, value],
  )

  return (
    <span css={[styles.root, overrides?.root]} className="hero-radio__root">
      <span
        css={[styles.innerBox, overrides?.innerBox]}
        className="hero-radio__innerBox"
      >
        <input
          className="hero-radio__input"
          type="radio"
          data-testid="hero-radio"
          {...inputProps}
          name={name}
          value={value}
          css={[styles.input, overrides?.input, ...disabledStyles]}
          disabled={disabled}
          checked={checked}
          onChange={onChange}
          ref={forkRefs}
        />
        <div
          css={[styles.visualRadio, overrides?.visualRadio]}
          className="hero-radio__visualRadio"
        >
          <CheckedIcon
            css={[styles.icon, overrides?.icon]}
            className="hero-radio__icon"
          />
        </div>
      </span>
    </span>
  )
}
